import type { IContact } from "@bbj-types/contact";
import { ContactType } from "@bbj-types/contact/enum";
import { AutocompletePlaces } from "@inputs/autocomplete/AutocompletePlacesUniq";
import { FormHelperText, Stack, TextField, type TextFieldProps, Typography } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { PhoneInputV2, StackGrid, stackGridClasses } from "@styleguide";
import type React from "react";
import { useEffect } from "react";
import { Controller, type UseFormReturn, useForm } from "react-hook-form";
import { type TContactForm, resolver } from "./logics";

interface IContactFormProps {
  onSubmit: (data: TContactForm, hookForm: UseFormReturn<TContactForm>) => void;
  children?: React.FC<{ hookForm: UseFormReturn<TContactForm> }>;
  contact?: IContact;
}

const commonProps: Partial<TextFieldProps> = {
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

export const ContactForm = ({ children, contact, onSubmit }: IContactFormProps) => {
  const { user } = useGlobalContext();
  const hookForm = useForm<TContactForm>({
    defaultValues: contact
      ? { ...contact, type: ContactType.ENTERPRISE }
      : { place: user.organisation.places, type: ContactType.ENTERPRISE },
    shouldUnregister: true,
    resolver,
  });
  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = hookForm;

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    reset(
      contact
        ? { ...contact, type: ContactType.ENTERPRISE }
        : { place: user.organisation.places, type: ContactType.ENTERPRISE }
    );
  }, [contact]);

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data, hookForm))}>
      <Stack direction="column" spacing={1}>
        <StackGrid rowGap={1} columnGap={1} perLine={2}>
          <TextField label="Prénom" {...commonProps} {...register("firstName")} />
          <TextField label="Nom" {...commonProps} {...register("lastName")} />
          <TextField label="Poste" {...commonProps} {...register("job")} />
          <TextField
            label="Email"
            {...commonProps}
            error={!!errors?.email}
            helperText={errors?.email?.type === "pattern" && "L'email renseigné n'est pas valide"}
            {...register("email", {
              // eslint-disable-next-line
              pattern: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$/,
            })}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
              <PhoneInputV2
                inputRef={ref}
                label="Téléphone"
                value={value ?? ""}
                onChange={onChange}
                size="small"
                fullWidth
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
          <Controller
            name="place"
            control={control}
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <AutocompletePlaces
                value={value || null}
                // defaultValue={contact?.place}
                onChange={(_, value) => onChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Zone géographique*"
                    size="small"
                    fullWidth
                    error={!!errors.place}
                    helperText={errors.place?.message}
                  />
                )}
              />
            )}
          />
          <FormHelperText
            className={stackGridClasses.fullWidth}
            sx={() => ({
              textAlign: "center",
              display: errors.phone?.type === "required" && errors.email?.type === "required" ? undefined : "none",
            })}
            error
            required
          >
            Veuillez entrer au moins un numéro de téléphone ou une adresse email.
          </FormHelperText>
        </StackGrid>
        <Typography component="p" variant="caption" sx={{ pt: 1 }}>
          Donnez le maximum d’informations sur ce contact : quelle est la nature de la relation ? comment transmettre
          les candidatures ? etc.
        </Typography>
        <TextField label="Commentaires" multiline rows={3} {...commonProps} {...register("description")} />
        <Typography component="p" variant="caption">
          Pour un contact, il faut a minima un email ou un téléphone.
        </Typography>
      </Stack>
      {children?.({ hookForm })}
    </form>
  );
};
